import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FiMail, FiMapPin, FiLinkedin, FiGithub, FiSend, FiX } from 'react-icons/fi';
import emailjs from '@emailjs/browser';

const ContactSection = styled.section`
  padding: 6rem 4rem;
  position: relative;
  background: var(--background-dark);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Title = styled(motion.h2)`
  font-family: var(--font-display);
  font-size: 2.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1rem;
  color: var(--text-primary);
  position: relative;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 2.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ContactGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
`;

const ContactMeta = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  color: var(--text-secondary);
  font-size: 1.05rem;

  span {
    display: inline-flex;
    align-items: center;
  }
`;

const CopyEmail = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text-primary);
  }

  .copied-flag {
    color: var(--text-primary);
    font-size: 0.85rem;
    opacity: 0.8;
  }
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--text-muted);
  opacity: 0.5;

  @media (max-width: 480px) {
    display: none;
  }
`;

const ContactInfo = styled(motion.div)`
  space-y: 2rem;
`;

const ContactCard = styled(motion.div)`
  background: transparent;
  padding: 2rem 0;
  margin-bottom: 2rem;
  transition: all var(--transition-fast);
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ContactIcon = styled.div`
  color: var(--primary-color);
  margin-right: 1rem;
  flex-shrink: 0;
`;

const ContactDetails = styled.div``;

const ContactTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const ContactValue = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: 1.5px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--text-primary);
    color: var(--text-primary);
    transform: translateY(-2px);
  }
`;

const ContactForm = styled(motion.form)`
  background: transparent;
  padding: 2rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 1rem;
  transition: border-color 0.15s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:focus {
    outline: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  }
  
  &::placeholder {
    color: var(--text-muted);
    opacity: 0.6;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 1rem;
  transition: border-color 0.15s ease;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:focus {
    outline: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  }
  
  &::placeholder {
    color: var(--text-muted);
    opacity: 0.6;
  }
`;

const SubmitButton = styled(motion.button)`
  background: transparent;
  border: 1.5px solid var(--text-primary);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.75rem 2rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--text-primary);
    color: white;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Styled component for the overlay
const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(13, 17, 23, 0.9);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const OverlayCard = styled(motion.div)`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
`;

const OverlayIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;

const OverlayTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const OverlayMessage = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const EmailButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1.5px solid var(--text-primary);
  color: var(--text-primary);
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--text-primary);
    color: white;
    transform: translateY(-2px);
  }
`;

const CooldownText = styled.p`
  color: var(--text-muted);
  font-size: 0.85rem;
  margin-top: 1.5rem;
`;

const SuccessOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(250, 250, 248, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const SuccessCard = styled(motion.div)`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 2.5rem;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 1rem;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;

const CountdownText = styled.p`
  color: var(--text-muted);
  font-size: 0.85rem;
  margin-top: 1rem;
`;

const ErrorText = styled(motion.div)`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  opacity: 0.8;
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Advanced fingerprinting function - moved outside component
const createFingerprint = () => {
  const nav = window.navigator;
  const screen = window.screen;
  const fingerprint = {
    userAgent: nav.userAgent,
    language: nav.language,
    platform: nav.platform,
    screenRes: `${screen.width}x${screen.height}`,
    colorDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    plugins: nav.plugins.length,
    cookieEnabled: nav.cookieEnabled,
    doNotTrack: nav.doNotTrack,
    timestamp: Date.now()
  };
  return btoa(JSON.stringify(fingerprint)).substring(0, 32);
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [emailCopied, setEmailCopied] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [countdown, setCountdown] = useState(2);
  const [emailError, setEmailError] = useState('');
  const [shakeError, setShakeError] = useState(false);

  const handleCopyEmail = async () => {
    const email = 'dev.aldenb@gmail.com';
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = email;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* no-op */ }
      document.body.removeChild(ta);
    }
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init("9HQiVvmRzFD-Kf0L8");
    // Removed automatic overlay check - overlay should only appear when trying to send 3rd message
  }, []); // Only run once on mount

  // Handle escape key to close overlay
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showOverlay) {
        setShowOverlay(false);
      }
    };

    if (showOverlay) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showOverlay]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear email error when user types
    if (e.target.name === 'email' && emailError) {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if email is empty
    if (!formData.email) {
      setEmailError('Please enter your email address');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      document.getElementById('email').focus();
      return;
    }
    
    // Validate email format
    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      document.getElementById('email').focus();
      return;
    }
    
    // Check other required fields
    if (!formData.name || !formData.subject || !formData.message) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all required fields'
      });
      return;
    }
    
    // Check submission limit BEFORE sending
    const fingerprint = createFingerprint();
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '{}');
    const currentCount = submissions[fingerprint]?.count || 0;
    
    if (currentCount >= 2) {
      setShowOverlay(true);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });
    
    try {
      // Send email using EmailJS
      const result = await emailjs.send(
        "service_y8rbwot",
        "template_b88npxd",
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_email: "dev.aldenb@gmail.com",
          time: new Date().toLocaleString(),
        },
        {
          publicKey: "9HQiVvmRzFD-Kf0L8"
        }
      );

      if (result.status === 200) {
        // Track submission with count (we already have fingerprint and submissions from above)
        submissions[fingerprint] = {
          timestamp: new Date().toISOString(),
          email: formData.email,
          name: formData.name,
          count: currentCount + 1
        };
        localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
        
        // Show success overlay instead of inline message
        setShowSuccessOverlay(true);
        setCountdown(2);
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        // Start countdown
        const countdownInterval = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              setShowSuccessOverlay(false);
              
              // Show spam overlay if limit reached
              if (currentCount + 1 >= 2) {
                setTimeout(() => setShowOverlay(true), 500);
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, something went wrong. Please try again or email me directly at dev.aldenb@gmail.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <Container>
        <Title
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Get In Touch
        </Title>
        
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          I'm always excited to learn from new projects. Feel free to reach out about research, collaborations, or just to say hi.
        </Subtitle>
        
        <ContactGrid>
          {/* Email address links to the visitor's default mail app (mailto).
              The EmailJS contact form below is commented out (kept for easy restore). */}
          <ContactMeta
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <CopyEmail onClick={handleCopyEmail} title="Click to copy" aria-label="Copy email address">
              <FiMail size={16} style={{ verticalAlign: 'middle', opacity: 0.7 }} />
              dev.aldenb@gmail.com
              {emailCopied && <span className="copied-flag">Copied!</span>}
            </CopyEmail>
            <Dot />
            <span>
              <FiMapPin size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle', opacity: 0.7 }} />
              San Diego, California
            </span>
          </ContactMeta>

          <SocialLinks
            as={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <SocialLink
              href="https://www.linkedin.com/in/alden-bernstein-31b1a22b0/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiLinkedin size={18} />
            </SocialLink>
            <SocialLink
              href="https://github.com/aldenbernsteinn"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiGithub size={18} />
            </SocialLink>
          </SocialLinks>

          {/* ===== FORM VERSION (commented out) — EmailJS-based contact form. To restore, uncomment this block and remove the "Email Me" button above.
          <ContactForm
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            noValidate
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
            <FormGroup>
              <Label htmlFor="name">Name</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
              />
            </FormGroup>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
            >
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                placeholder="your.email@example.com"
              />
              {emailError && (
                <ErrorText
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    x: shakeError ? [0, -10, 10, -10, 10, 0] : 0
                  }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ 
                    duration: 0.3,
                    x: {
                      duration: 0.5,
                      ease: "easeInOut"
                    }
                  }}
                >
                  {emailError}
                </ErrorText>
              )}
            </FormGroup>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              viewport={{ once: true }}
            >
            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="What's this about?"
              />
            </FormGroup>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              viewport={{ once: true }}
            >
            <FormGroup>
              <Label htmlFor="message">Message</Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell me about your project or just say hello!"
              />
            </FormGroup>
            </motion.div>
            
            {submitStatus.message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  padding: '0.5rem 0',
                  marginBottom: '1rem',
                  color: submitStatus.type === 'success' 
                    ? 'var(--text-secondary)' 
                    : 'var(--text-secondary)',
                  opacity: submitStatus.type === 'success' ? 0.8 : 0.7,
                  fontSize: '0.95rem',
                  textAlign: 'left'
                }}
              >
                {submitStatus.message}
              </motion.div>
            )}
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              viewport={{ once: true }}
            >
              <SubmitButton
                type="submit"
                disabled={isSubmitting}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiSend size={18} />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </motion.div>
          </ContactForm>
          ===== */}
        </ContactGrid>
      </Container>
      
      {/* Overlay for users who have already submitted */}
      {showOverlay && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => setShowOverlay(false)}
        >
          <OverlayCard
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton
              onClick={() => setShowOverlay(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX size={20} />
            </CloseButton>
            
            <OverlayIcon>
              <FiMail size={40} />
            </OverlayIcon>
            
            <OverlayTitle>Message Already Sent</OverlayTitle>
            
            <OverlayMessage>
              Seems you have submitted a form recently. To prevent spam and ensure quality communication, 
              please use email directly for additional messages.
            </OverlayMessage>
            
            <EmailButton
              href="mailto:dev.aldenb@gmail.com"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiMail size={20} />
              Email dev.aldenb@gmail.com directly
            </EmailButton>
          </OverlayCard>
        </OverlayContainer>
      )}
      
      {/* Success Overlay */}
      <AnimatePresence>
        {showSuccessOverlay && (
          <SuccessOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <SuccessCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SuccessIcon>
                <FiMail size={28} />
              </SuccessIcon>
              
              <OverlayTitle style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>
                Thank You!
              </OverlayTitle>
              
              <OverlayMessage style={{ marginBottom: '1rem' }}>
                I'll get back to you soon.
              </OverlayMessage>
              
              <CountdownText>
                This window will close in {countdown}...
              </CountdownText>
            </SuccessCard>
          </SuccessOverlay>
        )}
      </AnimatePresence>
    </ContactSection>
  );
};

export default Contact;